import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { colorPrimary, radiusMd } from "@porsche-design-system/components-react/emotion"
import type { ImageSourceSet } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useRef, useState } from "react"
import { debounce } from "src/common/debounce"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Picture } from "../picture/Picture"

export interface CarouselImage {
	alt: string
	imageSourceSet: ImageSourceSet
	thumbnailAlt: string
}

interface ImageCarouselProps {
	images: CarouselImage[]
}

export const ImageCarousel = (props: ImageCarouselProps) => {
	const { images } = props

	const mainScroller = useRef<HTMLUListElement>(null)

	// currentSlide is not used to rerender or influence the behaviour of the main scroller, only to show the state in other UI elements.
	const [currentSlide, setCurrentSlide] = useState(0)

	const childCount = images?.length || 0

	const scrollMainToSlide = useCallback(
		(slide: number, animated = true) => {
			if (!mainScroller.current) {
				return
			}

			const left = Math.floor(mainScroller.current.scrollWidth * ((slide % childCount) / childCount))

			if (animated && mainScroller.current.scrollTo) {
				mainScroller.current.scrollTo({
					behavior: "smooth",
					left,
				})
			} else {
				mainScroller.current.scrollLeft = left
			}
		},
		[childCount],
	)

	useEffect(() => {
		scrollMainToSlide(0, false)
	}, [scrollMainToSlide])

	// Layout on resize
	useEffect(() => {
		const callback = debounce(() => {
			scrollMainToSlide(currentSlide)
		}, 100)

		window.addEventListener("resize", callback, { passive: true })

		return function cleanup() {
			window.removeEventListener("resize", callback)
		}
	}, [scrollMainToSlide, currentSlide])

	// Track currently active slide on scroll
	useEffect(() => {
		const element = mainScroller.current
		if (!element) {
			return
		}

		const callback = () => {
			const newSlide = Math.round((element.scrollLeft / element.scrollWidth) * childCount)

			if (newSlide !== currentSlide) {
				setCurrentSlide(newSlide)
			}
		}

		element.addEventListener("scroll", callback, { passive: true })

		return function cleanup() {
			element.removeEventListener("scroll", callback)
		}
	}, [childCount, currentSlide])

	const thumbnailSelected = (slide: number) => {
		scrollMainToSlide(slide, false)
	}

	if (images?.length === 0) {
		return null
	}

	return (
		<>
			<CarouselShell>
				<CarouselScroller ref={mainScroller}>
					{images?.map((image, imageIndex) => {
						return (
							<CarouselItem key={imageIndex}>
								{/* TODO: use PictureResponsive? (need to adjust bff first) */}
								<Picture aspectRatio="16:9" alt={image.alt} lazy>
									<source
										type="image/webp"
										srcSet={image.imageSourceSet.webp ?? undefined}
										sizes="50vw"
									/>
									<source type="image/jpeg" srcSet={image.imageSourceSet.jpg} sizes="50vw" />
								</Picture>
							</CarouselItem>
						)
					})}
				</CarouselScroller>
			</CarouselShell>

			{images && images.length > 1 && (
				<ThumbnailContainer>
					{images.map((image, imageIndex) => {
						return (
							<Thumbnail
								key={imageIndex}
								$active={imageIndex === currentSlide}
								onClick={() => thumbnailSelected(imageIndex)}
							>
								{/* TODO: use PictureResponsive? (need to adjust bff first) */}
								<Picture aspectRatio="16:9" alt={image.thumbnailAlt} lazy>
									<source
										type="image/webp"
										srcSet={image.imageSourceSet.webp ?? undefined}
										sizes="50vw"
									/>
									<source type="image/jpeg" srcSet={image.imageSourceSet.jpg} sizes="50vw" />
								</Picture>
							</Thumbnail>
						)
					})}
				</ThumbnailContainer>
			)}
		</>
	)
}

const CarouselShell = styled.div`
	width: 100%;
	overflow: hidden;
`

const CarouselScroller = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	display: flex;
	align-items: center;
	overflow-x: auto;
	overflow-y: hidden;
	width: 100%;

	-webkit-overflow-scrolling: touch;
	scroll-snap-type: x mandatory;

	-ms-overflow-style: none;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`

const CarouselItem = styled.li`
	width: 100%;
	height: 100%;
	flex-shrink: 0;
	flex-grow: 0;
	cursor: pointer;
	scroll-snap-align: center;
	scroll-snap-stop: always;

	> * {
		border-radius: var(--p-radius-xl) var(--p-radius-xl) 0 0;
	}
`

const ThumbnailContainer = styled.div`
	display: grid;
	grid-template-rows: auto;
	grid-gap: 2px;
	margin: 4px 4px 0;
	grid-template-columns: repeat(3, 1fr);

	@media (min-width: 480px) {
		grid-template-columns: repeat(4, 1fr);
	}

	@media (min-width: 640px) {
		grid-template-columns: repeat(5, 1fr);
	}

	@media (min-width: 780px) {
		grid-template-columns: repeat(6, 1fr);
	}
`

const buttonReset = css`
	outline: none;
	border: none;
	margin: 0;
	padding: 0;
	overflow: visible;
	border-radius: 0;
	background: transparent;
	text-align: center;
	text-decoration: none;
	color: ${colorPrimary};
	cursor: pointer;

	-webkit-font-smoothing: inherit;
	-moz-osx-font-smoothing: inherit;

	appearance: none;
	-webkit-appearance: none;
`

const Thumbnail = styled("button", { shouldForwardProp })<{ $active: boolean }>`
	${buttonReset};
	box-sizing: border-box;
	display: block;
	border: ${(props) => (props.$active ? `2px solid ${colorPrimary}` : "2px solid transparent")};
	border-radius: calc(${radiusMd} + 2px);

	@media (hover: hover) and (pointer: fine) {
		&:hover {
			border: 2px solid ${colorPrimary};
		}
	}

	&:focus-visible {
		border: 2px solid ${colorPrimary};
	}
`
